import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { QawaemErpModalFormTabBar } from '../../products/saudi/components/QawaemErpModalFormTabs';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import { AppNotificationToast, useAppNotificationToast } from '../../shared/components/AppNotificationToast';
import { notifyBulkDeleted, notifyCreated, notifyDeleted, notifyError, notifyUpdated } from '../../shared/utils/crudNotify';
import { sortRecordsNewestFirst } from '../../shared/utils/listSortUtils';
import localApiAdapter from '../../core/services/localApiAdapter';
import { CustomDatePicker, CustomSelect, CustomNumberInput } from '../../shared/components/custom';
import { formatBrandMoney, getBrandLocale } from '../../shared/utils/brandCurrency';
import {
  SAUDI_REGISTER_LABEL_KEYS,
  SAUDI_DEFAULT_REGISTERS,
  SAUDI_DEFAULT_BANK_ACCOUNTS,
  SAUDI_TRANSFER_MOTIF_KEYS,
} from './saudiTreasuryDefaults';
import {
  mapTransferFromApi,
  mapTransferToApi,
  resolveCaisseId,
  formatBankAccountLabel,
} from './treasuryApiMappers';
import {
  transferExportHeaders,
  mapTransfersForExport,
  exportTreasuryCSV,
  exportTreasuryExcel,
  exportTreasuryPDF,
  transferImportHeaders,
  triggerTreasuryImport,
  parseTreasuryImport,
} from './treasuryExportHelpers';
import {
  ArrowRightLeft,
  Plus,
  Search,
  Calendar,
  ChevronDown,
  ChevronUp,
  Filter,
  Download,
  Upload,
  FileText,
  FileSpreadsheet,
  FileOutput,
  List,
  Grid,
  RefreshCcw,
  CheckCircle,
  Clock,
  Banknote,
  X,
  LayoutList,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  DollarSign,
  LayoutGrid,
  Shield,
  Layers,
  TrendingUp,
  Trash2,
} from 'lucide-react';
import '../../shared/styles/GPSTrackingDatePicker.css';
import useModuleConfigTheme from '../../shared/hooks/useModuleConfigTheme';
import {
  QAWAEM_MODAL_OVERLAY_CLASS,
  getQawaemModalPanelClass,
  getQawaemModalPanelStyle,
} from '../../products/saudi/components/QawaemErpCrudModal';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import {
  QawaemTableSelectAllHeader,
  QawaemTableSelectRowCell,
  useQawaemFilteredRowSelection,
} from '../../products/saudi/components/QawaemTableSelection';
import QawaemClickableStatusBadge from '../../products/saudi/components/QawaemClickableStatusBadge';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import TableColumnHeaderFilter from '../../shared/components/table/TableColumnHeaderFilter';
import useColumnHeaderFilters from '../../shared/hooks/useColumnHeaderFilters';
import applyColumnFilters, { textColumnRule, exactColumnRule } from '../../shared/utils/columnHeaderFilters';
import QawaemPageTitleWithFilters from '../../products/saudi/components/QawaemPageTitleWithFilters';
import {
  buildCaisseColumnDefs,
  useCaisseColumnVisibility,
  caisseTableColSpan,
  buildCaisseModalFormTabs,
  nextCaisseEntityRef,
  sliceCaisseListItems,
  useCaisseEmployeeOptions,
  buildCaisseEmployeeSelectOptions,
  CAISSE_FORM_GRID,
  useCaisseAccounts,
} from './caisseListPageUtils';
import { useSettings } from '../../core/contexts/SettingsContext';
import { useTreasuryConfig, useTreasuryListViewMode } from './useTreasuryConfig';
import QawaemImportExportToolbar from '../../products/saudi/components/QawaemImportExportToolbar';
import { QawaemTableStickyActionsTd } from '../../products/saudi/components/QawaemTableStickyActions';
import { QawaemErpViewModal } from '../../products/saudi/components/QawaemErpCrudModal';

const FundsTransferPage = () => {
  const { t, i18n } = useTranslation();
  const { settings } = useSettings();
  const treasuryConfig = useTreasuryConfig();
  const [viewMode, setViewMode] = useTreasuryListViewMode(treasuryConfig);
  const { employeeOptions } = useCaisseEmployeeOptions({ valueField: 'name' });
  const { accounts: caisseAccountsLive } = useCaisseAccounts();
  const responsibleOptions = useMemo(
    () => buildCaisseEmployeeSelectOptions(employeeOptions, { emptyLabel: '' }),
    [employeeOptions],
  );
  const p = (key, opts) => t(`modules.caisse.pages.fundsTransferPage.${key}`, opts);
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();
  const entityLabel = p('entity_label', { defaultValue: 'التحويل' });
  const entityLabelPlural = p('entity_label_plural', { defaultValue: 'التحويلات' });
  const cfgTheme = useModuleConfigTheme();
  const isLight = cfgTheme.light;

  const translateAccount = (val) => {
    const labelKey = SAUDI_REGISTER_LABEL_KEYS[val];
    return labelKey ? p(labelKey) : val;
  };
  const dateLocale = getBrandLocale(i18n.language);
  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString(dateLocale, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };
  const formatAmount = (amount) => formatBrandMoney(amount);

  const [caisseAccounts, setCaisseAccounts] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (caisseAccountsLive.length) setCaisseAccounts(caisseAccountsLive);
  }, [caisseAccountsLive]);

  const caisseAccountOptions = useMemo(() => {
    if (caisseAccounts.length) {
      return caisseAccounts.map((a) => ({
        value: a.name,
        label: translateAccount(a.name),
        isDefault: true,
      }));
    }
    return SAUDI_DEFAULT_REGISTERS.map((r) => ({
      value: r.value,
      label: p(r.labelKey),
      isDefault: true,
    }));
  }, [caisseAccounts, p]);

  const bankAccountOptions = useMemo(() => {
    if (bankAccounts.length) {
      return bankAccounts.map((b) => {
        const label = formatBankAccountLabel(b);
        return { value: label, label, isDefault: true };
      });
    }
    return SAUDI_DEFAULT_BANK_ACCOUNTS.map((b) => ({
      value: b.value,
      label: p(b.labelKey),
      isDefault: true,
    }));
  }, [bankAccounts, p]);

  const sourceAccountOptions = caisseAccountOptions;
  const destinationAccountOptions = useMemo(
    () => [...caisseAccountOptions, ...bankAccountOptions],
    [caisseAccountOptions, bankAccountOptions],
  );
  const motifOptions = SAUDI_TRANSFER_MOTIF_KEYS.map((key) => ({
    value: p(key),
    label: p(key),
    isDefault: true,
  }));
  const statusOptions = [
    { value: 'pending', label: p('status_pending'), isDefault: true },
    { value: 'completed', label: p('status_completed'), isDefault: true },
    { value: 'cancelled', label: p('status_cancelled'), isDefault: true },
  ];

  // ===== STATE MANAGEMENT =====
  const [searchTerm, setSearchTerm] = useState('');
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  
  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedTransfer, setSelectedTransfer] = useState(null);
  
  const [currentView, setCurrentView] = useState('all');
  const [quickFilter, setQuickFilter] = useState('all');
  
  const [transfers, setTransfers] = useState([]);
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    reference: '',
    source: '',
    destination: '',
    montant: 0,
    motif: '',
    responsable: '',
    status: ''
  });
  
  const importDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);

  const { filters, setFilter, editingKey, setEditingKey } = useColumnHeaderFilters();

  const columnFilterRules = useMemo(() => [
    textColumnRule('reference', (r) => r.reference || ''),
    textColumnRule('date', (r) => formatDate(r.date)),
    textColumnRule('source', (r) => translateAccount(r.source)),
    textColumnRule('destination', (r) => translateAccount(r.destination)),
    textColumnRule('amount', (r) => String(r.montant ?? '')),
    textColumnRule('responsible', (r) => r.responsable || ''),
    exactColumnRule('status', (r) => r.status || ''),
  ], []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target)) {
        setShowImportDropdown(false);
      }
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) {
        setShowExportDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadAccounts = useCallback(async () => {
    try {
      const dashboard = await localApiAdapter.get('/tresorerie/position/by-type/caisse');
      const accounts = dashboard?.accounts || dashboard?.balanceByCaisse || [];
      setCaisseAccounts(accounts);
      try {
        const banks = await localApiAdapter.get('/banking/accounts');
        setBankAccounts(Array.isArray(banks) ? banks : []);
      } catch {
        setBankAccounts([]);
      }
    } catch (error) {
      console.error('Failed to load caisse accounts:', error);
    }
  }, []);

  const loadTransfers = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await localApiAdapter.get('/treasury/transfers');
      setTransfers(sortRecordsNewestFirst((Array.isArray(rows) ? rows : []).map(mapTransferFromApi)));
    } catch (error) {
      console.error('Failed to load transfers:', error);
      toast.error(p('load_error') || t('common.load_error'));
      setTransfers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAccounts();
    loadTransfers();
  }, [loadAccounts, loadTransfers]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedTransfer) {
        if (formData.status && formData.status !== selectedTransfer.status) {
          await localApiAdapter.patch(`/treasury/transfers/${selectedTransfer.id}/status`, {
            status: formData.status,
          });
        }
        notifyUpdated(showNotify, entityLabel);
      } else {
        const reference = formData.reference || nextCaisseEntityRef(
          settings,
          'transfer',
          treasuryConfig.transferPrefix || 'TRF',
          transfers,
          (t) => t.reference,
        );
        const payload = mapTransferToApi({ ...formData, reference }, caisseAccounts, bankAccounts);
        if (!resolveCaisseId(formData.source, caisseAccounts) && !caisseAccounts.length) {
          notifyError(showNotify, p('select_source'));
          return;
        }
        await localApiAdapter.post('/treasury/transfers', payload);
        notifyCreated(showNotify, entityLabel);
      }
      await loadTransfers();
      resetForm();
    } catch (error) {
      console.error('Transfer save error:', error);
      notifyError(showNotify, error.message || t('common.error'));
    }
  };

  const handleEdit = (transfer) => {
    setSelectedTransfer(transfer);
    setFormData(transfer);
    setTransferFormTab('transfer');
    setShowModal(true);
  };

  const handleView = (transfer) => {
    setSelectedTransfer(transfer);
    setShowViewModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm(p('delete_confirm_single'))) return;
    try {
      await localApiAdapter.patch(`/treasury/transfers/${id}/status`, { status: 'cancelled' });
      notifyDeleted(showNotify, entityLabel);
      await loadTransfers();
    } catch (error) {
      console.error('Transfer delete error:', error);
      notifyError(showNotify, error.message || t('common.error'));
    }
  };

  const handleBulkDelete = async () => {
    if (!selectedRecords.length || !window.confirm(p('delete_confirm_bulk', { count: selectedRecords.length, defaultValue: `Delete ${selectedRecords.length} items?` }))) return;
    try {
      await Promise.all(selectedRecords.map((id) => localApiAdapter.patch(`/treasury/transfers/${id}/status`, { status: 'cancelled' })));
      const deleteCount = selectedRecords.length;
      clearSelection();
      notifyBulkDeleted(showNotify, deleteCount, entityLabelPlural);
      await loadTransfers();
    } catch (error) {
      console.error('Transfer bulk delete error:', error);
      notifyError(showNotify, error.message || t('common.error'));
    }
  };

  const [transferFormTab, setTransferFormTab] = useState('transfer');

  const transferFormTabDefs = useMemo(() => ([
    { id: 'transfer', label: p('section_transfer_info'), icon: ArrowRightLeft, iconClassName: 'text-emerald-400' },
    { id: 'details', label: p('section_financial_info'), icon: DollarSign, iconClassName: 'text-yellow-400' },
  ]), [p]);
  const TRANSFER_FIELD_COUNT = 8;
  const transferFormTabs = useMemo(
    () => buildCaisseModalFormTabs(transferFormTabDefs, TRANSFER_FIELD_COUNT),
    [transferFormTabDefs],
  );

  const buildNewTransferForm = () => ({
    date: new Date().toISOString().split('T')[0],
    reference: nextCaisseEntityRef(
      settings,
      'transfer',
      treasuryConfig.transferPrefix || 'TRF',
      transfers,
      (t) => t.reference,
    ),
    source: '',
    destination: '',
    montant: 0,
    motif: '',
    responsable: '',
    status: '',
  });

  const resetForm = () => {
    setFormData(buildNewTransferForm());
    setSelectedTransfer(null);
    setTransferFormTab('transfer');
    setShowModal(false);
  };

  const stats = {
    total: transfers.length,
    completed: transfers.filter(t => t.status === 'completed').length,
    pending: transfers.filter(t => t.status === 'pending').length,
    totalAmount: transfers.reduce((sum, t) => sum + t.montant, 0)
  };

  const filteredTransfers = applyColumnFilters(
    transfers.filter((transfer) => {
      const matchesView = currentView === 'all' || transfer.status === currentView;
      const matchesSearch = !searchTerm ||
        transfer.reference?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        transfer.source?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        transfer.destination?.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesView && matchesSearch;
    }),
    filters,
    columnFilterRules,
  );
  const pagedTransfers = useMemo(
    () => sliceCaisseListItems(filteredTransfers, treasuryConfig.itemsPerPage),
    [filteredTransfers, treasuryConfig.itemsPerPage],
  );

  const {
    selectedRecords,
    allRowsSelected,
    someRowsSelected,
    handleSelectAll,
    handleSelectRecord,
    clearSelection,
  } = useQawaemFilteredRowSelection(filteredTransfers);

  const clickableStatusOptions = useMemo(() => ([
    { value: 'pending', label: p('status_pending'), icon: Clock, style: 'bg-amber-500/20 text-amber-300 border-amber-500/30' },
    { value: 'completed', label: p('status_completed'), icon: CheckCircle, style: 'bg-green-500/20 text-green-300 border-green-500/30' },
    { value: 'cancelled', label: p('status_cancelled'), icon: X, style: 'bg-red-500/20 text-red-300 border-red-500/30' },
  ]), [p]);

  const transferColumnKeys = useMemo(
    () => ['reference', 'date', 'source', 'destination', 'amount', 'responsible', 'status', 'actions'],
    [],
  );
  const { columnVisibility, handleToggleColumn, isColumnVisible } = useCaisseColumnVisibility(transferColumnKeys);
  const qawaemColumns = useMemo(
    () => buildCaisseColumnDefs([
      { key: 'reference', labelKey: 'col_reference' },
      { key: 'date', labelKey: 'col_date' },
      { key: 'source', labelKey: 'col_source' },
      { key: 'destination', labelKey: 'col_destination' },
      { key: 'amount', labelKey: 'col_amount' },
      { key: 'responsible', labelKey: 'col_responsible' },
      { key: 'status', labelKey: 'col_status' },
      { key: 'actions', labelKey: 'col_actions', required: true },
    ], p, columnVisibility),
    [p, columnVisibility],
  );
  const qawaemVisibleColumnCount = qawaemColumns.filter((c) => c.checked !== false).length;
  const tableColSpan = caisseTableColSpan(qawaemColumns);

  const handleStatusChange = async (recordId, newStatus) => {
    try {
      await localApiAdapter.patch(`/treasury/transfers/${recordId}/status`, { status: newStatus });
      notifyUpdated(showNotify, entityLabel);
      await loadTransfers();
    } catch (error) {
      console.error('Transfer status change error:', error);
      notifyError(showNotify, error.message || t('common.error'));
    }
  };

  const getStatusBadge = (status) => {
    const config = clickableStatusOptions.find((s) => s.value === status) || clickableStatusOptions[0];
    const { label, icon: Icon, style } = config;
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium border inline-flex items-center gap-1.5 ${style}`}>
        {Icon ? <Icon size={14} /> : null}
        {label}
      </span>
    );
  };

  const viewFilterLabel = (view) => {
    if (view === 'all') return p('filter_all');
    if (view === 'pending') return p('filter_pending');
    if (view === 'completed') return p('filter_completed');
    return p('filter_cancelled');
  };

  const exportRows = mapTransfersForExport(filteredTransfers);
  const exportHeaders = transferExportHeaders(p);
  const exportBaseName = `treasury_transfers_${new Date().toISOString().slice(0, 10)}`;

  const handleExportCSV = () => {
    if (!exportRows.length) { toast.error(p('empty_message') || p('load_error')); return; }
    exportTreasuryCSV(exportRows, exportHeaders, `${exportBaseName}.csv`);
    setShowExportDropdown(false);
  };

  const handleExportExcel = async () => {
    if (!exportRows.length) { toast.error(p('empty_message') || p('load_error')); return; }
    await exportTreasuryExcel(exportRows, exportHeaders, `${exportBaseName}.xlsx`, p('title'));
    setShowExportDropdown(false);
  };

  const handleExportPDF = async () => {
    if (!exportRows.length) { toast.error(p('empty_message') || p('load_error')); return; }
    setIsGeneratingPDF(true);
    try {
      await exportTreasuryPDF(p('title'), exportHeaders, exportRows);
    } finally {
      setIsGeneratingPDF(false);
      setShowExportDropdown(false);
    }
  };

  const handleImportFile = (file) => {
    const headers = transferImportHeaders(p);
    const schema = {
      requiredFields: ['date', 'source', 'destination', 'montant'],
      validators: { montant: (v) => !Number.isNaN(Number(v)) },
    };
    parseTreasuryImport(file, headers, schema)
      .then(async (result) => {
        if (!result.success) {
          toast.error(result.errors?.[0] || p('load_error'));
          return;
        }
        let imported = 0;
        for (const row of result.data) {
          const payload = mapTransferToApi(
            {
              date: row.date,
              source: row.source,
              destination: row.destination,
              montant: row.montant,
              motif: row.motif || '',
            },
            caisseAccounts,
            bankAccounts
          );
          await localApiAdapter.post('/treasury/transfers', payload);
          imported += 1;
        }
        toast.success(p('import_success', { count: imported }));
        await loadTransfers();
      })
      .catch((err) => {
        console.error('Import transfers error:', err);
        toast.error(p('load_error'));
      })
      .finally(() => setShowImportDropdown(false));
  };

  return (
    <div className="space-y-6 clients-container">
      <div className="flex justify-between items-center list-toolbar-mobile-layout">
        <div className="flex-1 min-w-0">
          <QawaemPageTitleWithFilters
            filters={selectedRecords.length > 0 ? (
              <button
                type="button"
                onClick={handleBulkDelete}
                className="flex items-center gap-2 px-3 py-2 rounded-xl border border-red-500/35 bg-red-500/10 text-red-700 hover:bg-red-500/20 transition-colors text-sm font-medium shrink-0"
                title={p('delete_selection')}
              >
                <Trash2 size={16} />
                <span>{t('common.delete')} ({selectedRecords.length})</span>
              </button>
            ) : null}
          >
            <h1 className="text-3xl font-bold text-white">
              {p('title')}
              <button onClick={() => { loadTransfers(); loadAccounts(); }} className="refresh-button ml-4" title={t('common.refresh')}>
                <RefreshCcw size={20} />
              </button>
            </h1>
          </QawaemPageTitleWithFilters>
          <p className="text-white/60 mt-2">{p('subtitle')}</p>
        </div>
        <div className="flex gap-3 items-center">
          <QawaemImportExportToolbar
            importOpen={showImportDropdown}
            exportOpen={showExportDropdown}
            onToggleImport={() => { setShowImportDropdown(!showImportDropdown); setShowExportDropdown(false); }}
            onToggleExport={() => { setShowExportDropdown(!showExportDropdown); setShowImportDropdown(false); }}
            importRef={importDropdownRef}
            exportRef={exportDropdownRef}
            importLabel={p('import', { defaultValue: 'استيراد' })}
            exportLabel={p('export', { defaultValue: 'تصدير' })}
            generatingLabel={t('common.loading')}
            isGeneratingPDF={isGeneratingPDF}
            onImportCSV={() => { triggerTreasuryImport(handleImportFile, '.csv'); setShowImportDropdown(false); }}
            onImportExcel={() => { triggerTreasuryImport(handleImportFile); setShowImportDropdown(false); }}
            onExportCSV={handleExportCSV}
            onExportExcel={handleExportExcel}
            onExportPDF={handleExportPDF}
          />
          <button
            onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
            className={`${
              showAdvancedOptions 
                ? 'bg-gradient-to-r from-purple-500 to-purple-600 border-purple-400' 
                : 'bg-white/5 border-white/20'
            } border hover:bg-white/10 text-white px-4 py-3 rounded-xl font-semibold hover:border-white/30 transition-all duration-300 flex items-center space-x-2`}
            title={p('advanced_options')}
          >
            <Filter className="h-5 w-5" />
            <span className="hidden sm:inline">{p('advanced_options')}</span>
            {showAdvancedOptions ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
          
          <button
            onClick={() => { setSelectedTransfer(null); setFormData(buildNewTransferForm()); setTransferFormTab('transfer'); setShowModal(true); }}
            className="bg-gradient-to-r from-blue-500 to-blue-700 text-white px-6 py-3 rounded-xl font-semibold hover:from-blue-600 hover:to-blue-800 transition-all duration-300 flex items-center space-x-2"
          >
            <Plus className="h-5 w-5" />
            <span>{p('new_transfer')}</span>
          </button>
        </div>
      </div>

      {/* Stats Cards - EXACT GPS Glow Effect from FacturiersPage lines 502-523 */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {[
          { title: p('stat_total'), value: stats.total, icon: ArrowRightLeft, color: 'from-blue-500 to-blue-600' },
          { title: p('stat_completed'), value: stats.completed, icon: CheckCircle, color: 'from-green-500 to-green-600' },
          { title: p('stat_pending'), value: stats.pending, icon: Clock, color: 'from-amber-500 to-amber-600' },
          { title: p('stat_total_amount'), value: formatAmount(stats.totalAmount), icon: Banknote, color: 'from-purple-500 to-purple-600' },
          { title: p('stat_completion_rate'), value: stats.total > 0 ? `${Math.round((stats.completed / stats.total) * 100)}%` : '0%', icon: TrendingUp, color: 'from-emerald-500 to-emerald-600' }
        ].slice(0, 4).map((stat, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }} className="relative group">
            <div className={`absolute -inset-0.5 bg-gradient-to-r ${stat.color} rounded-3xl opacity-0 group-hover:opacity-50 transition-opacity duration-500 blur-sm`}></div>
            <div className="relative bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
              <div className="flex items-center justify-between">
                <div><p className="text-white/60 text-sm font-medium">{stat.title}</p><p className="text-2xl font-bold text-white force-white mt-2">{stat.value}</p></div>
                <div className="relative">
                  <div className={`absolute inset-0 bg-gradient-to-br ${stat.color} rounded-2xl opacity-0 group-hover:opacity-20 transition-opacity duration-300 blur-lg`}></div>
                  <div className={`relative p-3 rounded-2xl bg-gradient-to-r ${stat.color}`}><stat.icon className="h-6 w-6 text-white force-white" /></div>
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Advanced Options */}
      <AnimatePresence>
        {showAdvancedOptions && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.3 }} className="overflow-hidden">
            <div className="space-y-4">
              <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
                <div className="flex space-x-1 overflow-x-auto hide-scrollbar">
                  {['all', 'pending', 'completed', 'cancelled'].map(view => (
                    <button key={view} onClick={() => setCurrentView(view)} className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 whitespace-nowrap ${currentView === view ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}>
                      <span>{viewFilterLabel(view)}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Data Table with Integrated Header - EXACT GPS from FacturiersPage lines 581-869 */}
      <div className="glass-panel glass-table-shell overflow-visible">
        {viewMode === 'table' ? (
          <div className="overflow-x-auto table-horizontal-scroll">
            <table className="w-full clients-table data-table">
              <thead className="glass-table-head">
                <tr>
                  <QawaemTableSelectAllHeader
                    allRowsSelected={allRowsSelected}
                    someRowsSelected={someRowsSelected}
                    onChange={handleSelectAll}
                    selectAllTitle={t('common.select_all', { defaultValue: 'تحديد الكل' })}
                    deselectAllTitle={t('common.deselect_all', { defaultValue: 'إلغاء التحديد' })}
                  />
                  {isColumnVisible('reference') ? (
                    <TableColumnHeaderFilter columnKey="reference" label={p('col_reference')} value={filters.reference ?? ''} onChange={(v) => setFilter('reference', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white font-semibold" />
                  ) : null}
                  {isColumnVisible('date') ? (
                    <TableColumnHeaderFilter columnKey="date" label={p('col_date')} value={filters.date ?? ''} onChange={(v) => setFilter('date', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white font-semibold" />
                  ) : null}
                  {isColumnVisible('source') ? (
                    <TableColumnHeaderFilter columnKey="source" label={p('col_source')} value={filters.source ?? ''} onChange={(v) => setFilter('source', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white font-semibold" />
                  ) : null}
                  {isColumnVisible('destination') ? (
                    <TableColumnHeaderFilter columnKey="destination" label={p('col_destination')} value={filters.destination ?? ''} onChange={(v) => setFilter('destination', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white font-semibold" />
                  ) : null}
                  {isColumnVisible('amount') ? (
                    <TableColumnHeaderFilter columnKey="amount" label={p('col_amount')} value={filters.amount ?? ''} onChange={(v) => setFilter('amount', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white font-semibold" />
                  ) : null}
                  {isColumnVisible('responsible') ? (
                    <TableColumnHeaderFilter columnKey="responsible" label={p('col_responsible')} value={filters.responsible ?? ''} onChange={(v) => setFilter('responsible', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white font-semibold" />
                  ) : null}
                  {isColumnVisible('status') ? (
                    <TableColumnHeaderFilter columnKey="status" label={p('col_status')} kind="select" options={statusOptions.map((o) => ({ value: o.value, label: o.label }))} value={filters.status ?? ''} onChange={(v) => setFilter('status', v)} align="center" editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white font-semibold" />
                  ) : null}
                  <QawaemTableHeaderActionsCell
                    thClassName="px-6 py-3 text-center text-white font-semibold text-sm whitespace-nowrap align-middle"
                    label={p('col_actions')}
                    columns={qawaemColumns}
                    onToggle={handleToggleColumn}
                    visibleCount={qawaemVisibleColumnCount}
                    totalCount={qawaemColumns.length}
                  />
                </tr>
              </thead>
              <tbody className="bg-white/5">
                {pagedTransfers.map((transfer) => (
                  <tr key={transfer.id} className="hover:bg-white/10 transition-colors border-b border-white/10">
                    <QawaemTableSelectRowCell
                      checked={selectedRecords.includes(transfer.id)}
                      onChange={() => handleSelectRecord(transfer.id)}
                    />
                    {isColumnVisible('reference') ? (
                      <td className="px-6 py-4 text-white font-semibold">{transfer.reference}</td>
                    ) : null}
                    {isColumnVisible('date') ? (
                      <td className="px-6 py-4 text-white/80">{formatDate(transfer.date)}</td>
                    ) : null}
                    {isColumnVisible('source') ? (
                      <td className="px-6 py-4 text-white/80">{translateAccount(transfer.source)}</td>
                    ) : null}
                    {isColumnVisible('destination') ? (
                      <td className="px-6 py-4 text-white/80">{translateAccount(transfer.destination)}</td>
                    ) : null}
                    {isColumnVisible('amount') ? (
                      <td className="px-6 py-4 text-white font-bold">{formatAmount(transfer.montant)}</td>
                    ) : null}
                    {isColumnVisible('responsible') ? (
                      <td className="px-6 py-4 text-white/80">{transfer.responsable || '-'}</td>
                    ) : null}
                    {isColumnVisible('status') ? (
                      <td className="px-6 py-4 text-center">
                        <div className="flex justify-center">
                          <QawaemClickableStatusBadge
                            status={transfer.status}
                            recordId={transfer.id}
                            options={clickableStatusOptions}
                            onChange={handleStatusChange}
                            title={t('common.change_status', { defaultValue: 'انقر لتغيير الحالة' })}
                          />
                        </div>
                      </td>
                    ) : null}
                    <QawaemTableStickyActionsTd>
                        <QawaemErpRowActions
                        onView={() => handleView(transfer)}
                        onEdit={() => handleEdit(transfer)}
                        onDelete={() => handleDelete(transfer.id)}
                        viewTitle={t('common.view')}
                        editTitle={t('common.edit')}
                        deleteTitle={t('common.delete')}
                      />
                    </QawaemTableStickyActionsTd>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-6 space-y-4">
            {filteredTransfers.length > 0 ? (
              pagedTransfers.map((transfer, index) => (
                <motion.div key={transfer.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05 }} className="bg-white/[0.03] rounded-2xl p-6 border border-white/20 hover:border-white/40 hover:bg-white/[0.05] transition-all h-full min-w-0">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-4 mb-4">
                        <h3 className="text-xl font-bold text-white flex items-center gap-2">
                          <ArrowRightLeft size={20} className="text-emerald-400" />
                          {transfer.reference}
                        </h3>
                      </div>
                      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('section_transfer_info')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_reference')}:</span> {transfer.reference}</div>
                            <div><span className="font-medium">{p('label_date')}:</span> {formatDate(transfer.date)}</div>
                            <div><span className="font-medium">{p('label_status')}:</span> {getStatusBadge(transfer.status)}</div>
                          </div>
                        </div>
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('section_accounts')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_source')}:</span> {translateAccount(transfer.source)}</div>
                            <div><span className="font-medium">{p('label_destination')}:</span> {translateAccount(transfer.destination)}</div>
                          </div>
                        </div>
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('label_amount')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium text-green-400">{p('label_amount')}:</span> <span className="text-white font-bold text-lg">{formatAmount(transfer.montant)}</span></div>
                          </div>
                        </div>
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('section_amount_responsible')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_responsible')}:</span> {transfer.responsable}</div>
                            {transfer.motif && <div><span className="font-medium">{p('label_motif')}:</span> {transfer.motif}</div>}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-2 ml-4">
                      <QawaemClickableStatusBadge
                        status={transfer.status}
                        recordId={transfer.id}
                        options={clickableStatusOptions}
                        onChange={handleStatusChange}
                      />
                      <QawaemErpRowActions
                        compact
                        size={14}
                        onView={() => handleView(transfer)}
                        onEdit={() => handleEdit(transfer)}
                        onDelete={() => handleDelete(transfer.id)}
                        viewTitle={t('common.view')}
                        editTitle={t('common.edit')}
                        deleteTitle={t('common.delete')}
                      />
                    </div>
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="text-center py-12"><ArrowRightLeft size={48} className="mx-auto mb-4 text-white/30" /><p className="text-white/60 text-lg">{p('empty_state')}</p></div>
            )}
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showModal && ReactDOM.createPortal(
        <div className={QAWAEM_MODAL_OVERLAY_CLASS} onClick={resetForm} role="presentation">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className={getQawaemModalPanelClass(isLight)} style={getQawaemModalPanelStyle(isLight, '1200px')} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-6 border-b border-white/10">
              <h2 className="text-2xl font-bold text-white">{selectedTransfer ? p('modal_edit') : p('modal_new')}</h2>
              <div className="flex items-center gap-3">
                <button type="submit" form="transfer-form" className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-blue-500 to-blue-700 text-white hover:from-blue-600 hover:to-blue-800 transition-all duration-200 font-semibold flex items-center gap-2 shadow-lg shadow-blue-500/30"><CheckCircle size={18} />{selectedTransfer ? p('btn_update') : p('btn_create')}</button>
                <button onClick={resetForm} className="text-white/70 hover:text-white transition-colors"><X size={24} /></button>
              </div>
            </div>
            {transferFormTabs.length > 0 ? (
              <QawaemErpModalFormTabBar
                tabs={transferFormTabs}
                activeTab={transferFormTab}
                onTabChange={setTransferFormTab}
                isLight={isLight}
                ariaLabel={selectedTransfer ? p('modal_edit') : p('modal_new')}
              />
            ) : null}
            <div className="overflow-y-auto p-6 flex-1 min-h-0">
              <form id="transfer-form" onSubmit={handleSubmit}>
                {(transferFormTabs.length === 0 || transferFormTab === 'transfer') && (
                  <div className={CAISSE_FORM_GRID}>
                    <div className="min-w-0">
                      <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_date')} <span className="qawaem-required-mark">*</span></label>
                      <CustomDatePicker value={formData.date} onChange={(dateString) => setFormData({ ...formData, date: dateString })} placeholder={p('select_date')} />
                    </div>
                    <div className="min-w-0">
                      <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_reference')} <span className="qawaem-required-mark">*</span></label>
                      <input type="text" required value={formData.reference} onChange={(e) => setFormData({ ...formData, reference: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all font-mono" placeholder={p('reference_placeholder')} />
                    </div>
                    <div className="min-w-0">
                      <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_source')} <span className="qawaem-required-mark">*</span></label>
                      <CustomSelect value={formData.source} onChange={(value) => setFormData({ ...formData, source: value })} options={sourceAccountOptions} placeholder={p('select_source')} searchable={true} creatable={false} autoSelect={false} />
                    </div>
                    <div className="min-w-0">
                      <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_destination')} <span className="qawaem-required-mark">*</span></label>
                      <CustomSelect value={formData.destination} onChange={(value) => setFormData({ ...formData, destination: value })} options={destinationAccountOptions} placeholder={p('select_destination')} searchable={true} creatable={false} autoSelect={false} />
                    </div>
                    {transferFormTabs.length === 0 ? (
                      <>
                        <div className="min-w-0">
                          <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_amount_sar')} <span className="qawaem-required-mark">*</span></label>
                          <CustomNumberInput step={0.01} value={formData.montant} onChange={(value) => setFormData({ ...formData, montant: value })} placeholder="0.00" />
                        </div>
                        <div className="min-w-0">
                          <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_motif')}</label>
                          <CustomSelect value={formData.motif} onChange={(value) => setFormData({ ...formData, motif: value })} options={motifOptions} placeholder={p('motif_placeholder')} searchable={true} creatable={true} autoSelect={false} />
                        </div>
                        <div className="min-w-0">
                          <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_responsible')} <span className="qawaem-required-mark">*</span></label>
                          <CustomSelect value={formData.responsable} onChange={(value) => setFormData({ ...formData, responsable: value })} options={responsibleOptions} placeholder={p('select_responsible')} searchable={true} creatable={false} autoSelect={false} />
                        </div>
                        <div className="min-w-0">
                          <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_status')}</label>
                          <CustomSelect value={formData.status} onChange={(value) => setFormData({ ...formData, status: value })} options={statusOptions} placeholder={p('select_status')} searchable={true} creatable={true} autoSelect={false} />
                        </div>
                      </>
                    ) : null}
                  </div>
                )}
                {transferFormTabs.length > 0 && transferFormTab === 'details' && (
                  <div className={CAISSE_FORM_GRID}>
                    <div className="min-w-0">
                      <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_amount_sar')} <span className="qawaem-required-mark">*</span></label>
                      <CustomNumberInput step={0.01} value={formData.montant} onChange={(value) => setFormData({ ...formData, montant: value })} placeholder="0.00" />
                    </div>
                    <div className="min-w-0">
                      <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_motif')}</label>
                      <CustomSelect value={formData.motif} onChange={(value) => setFormData({ ...formData, motif: value })} options={motifOptions} placeholder={p('motif_placeholder')} searchable={true} creatable={true} autoSelect={false} />
                    </div>
                    <div className="min-w-0">
                      <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_responsible')} <span className="qawaem-required-mark">*</span></label>
                      <CustomSelect value={formData.responsable} onChange={(value) => setFormData({ ...formData, responsable: value })} options={responsibleOptions} placeholder={p('select_responsible')} searchable={true} creatable={false} autoSelect={false} />
                    </div>
                    <div className="min-w-0">
                      <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_status')}</label>
                      <CustomSelect value={formData.status} onChange={(value) => setFormData({ ...formData, status: value })} options={statusOptions} placeholder={p('select_status')} searchable={true} creatable={true} autoSelect={false} />
                    </div>
                  </div>
                )}
              </form>
            </div>
          </motion.div>
        </div>,
        document.body
      )}

      {/* View Modal */}
      {showViewModal && selectedTransfer ? (
        <QawaemErpViewModal
          open={showViewModal}
          onClose={() => { setShowViewModal(false); setSelectedTransfer(null); }}
          title={p('modal_details')}
          icon={ArrowRightLeft}
          maxWidth="1100px"
        >
              <div className="space-y-4">
                <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                  <h3 className="text-white font-semibold mb-4 flex items-center gap-2"><ArrowRightLeft size={18} className="text-emerald-400" />{p('section_transfer_info')}</h3>
                  <div className={CAISSE_FORM_GRID}>
                    <div><p className="text-white/60 text-sm mb-1">{p('label_reference')}</p><p className="text-white font-medium">{selectedTransfer.reference}</p></div>
                    <div><p className="text-white/60 text-sm mb-1">{p('label_date')}</p><p className="text-white font-medium">{formatDate(selectedTransfer.date)}</p></div>
                    <div><p className="text-white/60 text-sm mb-1">{p('label_status')}</p>{getStatusBadge(selectedTransfer.status)}</div>
                    <div><p className="text-white/60 text-sm mb-1">{p('label_source')}</p><p className="text-white font-medium">{translateAccount(selectedTransfer.source)}</p></div>
                    <div><p className="text-white/60 text-sm mb-1">{p('label_destination')}</p><p className="text-white font-medium">{translateAccount(selectedTransfer.destination)}</p></div>
                    <div><p className="text-white/60 text-sm mb-1">{p('label_amount')}</p><p className="text-white font-bold text-2xl">{formatAmount(selectedTransfer.montant)}</p></div>
                    <div><p className="text-white/60 text-sm mb-1">{p('label_responsible')}</p><p className="text-white font-medium">{selectedTransfer.responsable}</p></div>
                    {selectedTransfer.motif ? (
                      <div><p className="text-white/60 text-sm mb-1">{p('label_motif')}</p><p className="text-white font-medium">{selectedTransfer.motif}</p></div>
                    ) : null}
                  </div>
                </div>
              </div>
        </QawaemErpViewModal>
      ) : null}
      <AppNotificationToast
        visible={notifyToast.visible}
        message={notifyToast.message}
        messageHtml={notifyToast.messageHtml}
        variant={notifyToast.variant}
        status={notifyToast.status}
        showActions={notifyToast.showActions}
        topLabel={notifyToast.topLabel}
        onDismiss={dismissNotify}
        placement="bottom"
      />
    </div>
  );
};

export default FundsTransferPage;


